import React, {
  useCallback,
  useContext, useEffect, useMemo, useState,
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Checkbox, FlagBlock } from 'anf-core-react';
import { useLazyQuery } from '@apollo/client';
import CheckoutPageContext from '../../../../context/CheckoutPageContext';
import AddressForm from '../../../Common/AddressForm/AddressForm';
import Tmnt from '../../../Tmnt/Tmnt';
import BILLING_ADDRESS_CONFIG_QUERY from '../../../../gql/billingAddressConfig.gql';
import { SHIP_MODE_TYPE } from '../../../../tools/constants';

const APIFPO_VALUE = 'APO/FPO';

export default function BillingAddressForm() {
  const {
    checkoutPageState,
  } = useContext(CheckoutPageContext);
  const [isCountryVisible, setIsCountryVisible] = useState(false);
  const [addressConfig, setAddressConfig] = useState(null);
  const [isSameAsShippingCheckVisible, setIsSameAsShippingCheckVisible] = useState(true);
  const {
    textFor,
    shippingAddress,
    allCountries,
    addressyApiKey,
    shipMethod,
  } = checkoutPageState;
  const { watch, control, reset } = useFormContext();
  const billingAddressSameAsShipping = watch('billingAddressSameAsShipping');
  const billingCountry = watch('addresses.billing.country');

  const updateBillingCountry = useCallback((country) => {
    reset((prev) => ({
      ...prev,
      billingAddressSameAsShipping: false,
      addresses: {
        ...prev.addresses,
        billing: {
          ...prev.addresses?.billing,
          country,
        },
      },
    }));
  }, [reset]);

  const [getBillingAddressConfig] = useLazyQuery(BILLING_ADDRESS_CONFIG_QUERY, {
    fetchPolicy: 'no-cache',
    context: { batch: true },
    ssr: false,
    onCompleted: (res) => {
      setAddressConfig(res.billingAddressConfig);
    },
  });

  const shippingCountry = useMemo(() => {
    if (!shippingAddress) return '';
    return shippingAddress.isAPOFPO ? APIFPO_VALUE : shippingAddress.country;
  }, [shippingAddress]);

  useEffect(() => {
    if (shipMethod?.shipModeType === SHIP_MODE_TYPE.HOME_DELIVERY) {
      reset((prev) => ({
        ...prev,
        billingAddressSameAsShipping: true,
      }));
      setIsSameAsShippingCheckVisible(true);
    } else {
      updateBillingCountry(shippingCountry);
      setIsSameAsShippingCheckVisible(false);
    }
  }, [shipMethod, reset, shippingCountry, updateBillingCountry]);

  useEffect(() => {
    if (shippingAddress) {
      getBillingAddressConfig({
        variables: {
          country: shippingCountry,
        },
      });
    }
    window.addEventListener('billing:country-selected', (event) => {
      getBillingAddressConfig({
        variables: {
          country: event.detail.country,
        },
      });
      setIsCountryVisible(false);
    });

    return () => {
      window.removeEventListener('billing:country-selected', () => { });
    };
  }, [getBillingAddressConfig, shippingAddress, shippingCountry, reset]);

  const addressConfigData = useMemo(() => {
    if (!addressConfig || addressConfig.addressFields.length === 0) {
      return null;
    }

    return {
      id: 'billing',
      fields: addressConfig.addressFields.map((field) => {
        const label = textFor?.[field.label]
          ?? { key: field.label, value: field.label };
        const error = textFor?.[field.errorMessage]
          ?? { key: field.errorMessage, value: field.errorMessage };

        if (field.id === 'billing-country' && isCountryVisible) {
          const options = allCountries.map((country) => {
            if (country.label === APIFPO_VALUE) {
              return {
                ...country,
                value: APIFPO_VALUE,
              };
            }
            return country;
          });
          return {
            ...field,
            visibility: true,
            options,
            label,
            error,
          };
        }
        return {
          ...field,
          label,
          error,
        };
      }),
    };
  }, [addressConfig, textFor, allCountries, isCountryVisible]);

  const errorTmnts = useMemo(() => ({
    firstNameMissing: textFor?.firstNameMissing,
    lastNameMissing: textFor?.lastNameMissing,
    nameMissing: textFor?.nameMissing,
    address1ErrorMessage: textFor?.address1ErrorMessage,
    cityMissing: textFor?.cityMissing,
    postalUSCodeErrorMessage: textFor?.postalUSCodeErrorMessage,
    postalCACodeErrorMessage: textFor?.postalCACodeErrorMessage,
    postalCodeErrorMessage: textFor?.postalCodeErrorMessage,
  }), [textFor]);

  const onChangeCountry = () => {
    setIsCountryVisible(true);
  };

  if (!addressConfigData) return null;

  return (
    <div className="billing-address-form">
      {isSameAsShippingCheckVisible && (
        <Controller
          name="billingAddressSameAsShipping"
          control={control}
          render={({ field }) => (
            <Checkbox
              description={textFor?.billToShippingAddress?.value}
              classList="same-as-shipping"
              id="billing-address-same-as-shipping"
              data-testid="billingAddressSameAsShipping"
              isChecked={field.value}
              {...field}
              onChange={(e) => {
                if (!e.target.checked) {
                  updateBillingCountry(shippingCountry);
                }
                field.onChange(e);
              }}
            />
          )}
        />
      )}
      {!billingAddressSameAsShipping && (
        <>
          {!isCountryVisible && (
            <div className="billig-address-selected-country">
              <FlagBlock
                countryCode={billingCountry}
                countryName={<Tmnt tmnt={textFor?.billingTo} />}
              />
              <span className="billing-country">{billingCountry}</span>
              <button className="button link-button" onClick={onChangeCountry} type="button">
                <Tmnt tmnt={textFor?.change} />
              </button>
            </div>
          )}
          <AddressForm
            addressConfig={addressConfigData}
            type="billing"
            fieldInFix="billing"
            countryCode={billingCountry}
            addressyEnabled={addressConfig.isAddressyEnabled}
            addressyKey={addressyApiKey}
            manualEntryItemLabel={textFor?.addressyManualEntry}
            errorTmnts={errorTmnts}
          />
        </>
      )}
    </div>
  );
}
